// Registration Form Validation
class RegistrationFormValidator {
  constructor(formElement) {
    this.form = formElement;
    this.isSubmitting = false;
    this.interactedFields = new Set();
    this.setupEventListeners();
    this.setupAccessibilityFeatures();
  }

  setupEventListeners() {
    // Validate on input/change events
    const inputs = this.form.querySelectorAll("input, select");
    inputs.forEach((input) => {
      // Mark field as interacted when user starts typing or selecting
      input.addEventListener("input", () => {
        this.interactedFields.add(input.name);
        this.clearFieldError(input);
      });

      // For select elements, mark as interacted on change
      if (input.tagName === "SELECT") {
        input.addEventListener("change", () => {
          this.interactedFields.add(input.name);
          this.validateField(input);
        });
      }

      // Only validate on blur if the field has been interacted with
      input.addEventListener("blur", () => {
        if (this.interactedFields.has(input.name)) {
          this.validateField(input);
        }
      });

      // Enhanced keyboard navigation support
      input.addEventListener("keydown", (e) => this.handleKeyNavigation(e));
    });

    // Validate on form submission
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
  }

  setupAccessibilityFeatures() {
    // Ensure all error containers have proper ARIA attributes
    const errorElements = this.form.querySelectorAll(".error-message");
    errorElements.forEach((element) => {
      if (!element.hasAttribute("role")) {
        element.setAttribute("role", "alert");
      }
      if (!element.hasAttribute("aria-live")) {
        element.setAttribute("aria-live", "polite");
      }
    });

    // Set up form-level ARIA attributes
    this.form.setAttribute("novalidate", "true");
  }

  handleKeyNavigation(event) {
    // Enhanced keyboard navigation for better accessibility
    if (event.key === "Enter" && event.target.tagName === "SELECT") {
      // Prevent form submission when navigating select options
      if (!event.target.value) {
        event.preventDefault();
      }
    }

    // Handle Escape key to clear errors and return focus
    if (event.key === "Escape") {
      if (event.target.classList.contains("error")) {
        this.clearFieldError(event.target);
      }
    }

    // Handle Tab navigation to ensure proper focus management
    if (event.key === "Tab") {
      // Let the browser handle tab navigation naturally
      // but ensure error states are properly announced
      setTimeout(() => {
        if (
          document.activeElement &&
          document.activeElement.classList.contains("error")
        ) {
          const errorElement = document.getElementById(
            `${document.activeElement.name.replace("_", "-")}-error`
          );
          if (errorElement && errorElement.textContent) {
            this.announceToScreenReader(`Error: ${errorElement.textContent}`);
          }
        }
      }, 100);
    }
  }

  validateField(field) {
    const fieldName = field.name;
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = "";

    // Clear previous error
    this.clearFieldError(field);

    // Validate based on field type
    switch (fieldName) {
      case "name":
        if (!value) {
          errorMessage = "Full name is required";
          isValid = false;
        } else if (value.length < 2) {
          errorMessage = "Name must be at least 2 characters long";
          isValid = false;
        } else if (value.length > 100) {
          errorMessage = "Name must be less than 100 characters";
          isValid = false;
        }
        break;

      case "email":
        if (!value) {
          errorMessage = "Email address is required";
          isValid = false;
        } else if (!this.isValidEmail(value)) {
          errorMessage = "Please enter a valid email address";
          isValid = false;
        } else if (value.length > 254) {
          errorMessage = "Email address is too long";
          isValid = false;
        }
        break;

      case "visa_category":
        if (!value) {
          errorMessage = "Please select a visa category";
          isValid = false;
        } else if (!this.isValidVisaCategory(value)) {
          errorMessage = "Please select a valid visa category";
          isValid = false;
        }
        break;

      case "country":
        if (!value) {
          errorMessage = "Please select your country";
          isValid = false;
        } else if (!this.isValidCountry(value)) {
          errorMessage = "Please select a valid country";
          isValid = false;
        }
        break;
    }

    if (!isValid) {
      this.showFieldError(field, errorMessage);
    }

    return isValid;
  }

  isValidEmail(email) {
    // More comprehensive email validation
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  isValidVisaCategory(category) {
    // Validate against available visa categories
    const validCategories = Array.from(
      this.form.querySelectorAll('#visa_category option[value]:not([value=""])')
    ).map((option) => option.value);
    return validCategories.includes(category);
  }

  isValidCountry(country) {
    // Validate against available countries
    const validCountries = Array.from(
      this.form.querySelectorAll('#country option[value]:not([value=""])')
    ).map((option) => option.value);
    return validCountries.includes(country);
  }

  showFieldError(field, message) {
    const errorElement = document.getElementById(
      `${field.name.replace("_", "-")}-error`
    );
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = "block";

      // Announce error to screen readers
      this.announceToScreenReader(message);
    }

    field.classList.add("error");
    field.setAttribute("aria-invalid", "true");

    // Set focus to the field if it's not already focused
    if (document.activeElement !== field) {
      field.focus();
    }
  }

  clearFieldError(field) {
    const errorElement = document.getElementById(
      `${field.name.replace("_", "-")}-error`
    );
    if (errorElement) {
      errorElement.textContent = "";
      errorElement.style.display = "none";
    }

    field.classList.remove("error");
    field.removeAttribute("aria-invalid");
  }

  announceToScreenReader(message) {
    // Create a temporary element for screen reader announcements
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "assertive");
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "sr-only";
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove the announcement after a short delay
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  validateForm() {
    const inputs = this.form.querySelectorAll(
      "input[required], select[required]"
    );
    let isFormValid = true;

    inputs.forEach((input) => {
      if (!this.validateField(input)) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  handleSubmit(event) {
    event.preventDefault();

    // Prevent double submission
    if (this.isSubmitting) {
      return;
    }

    // Clear any previous form-level messages
    this.clearFormMessages();

    // Validate all fields
    if (!this.validateForm()) {
      this.showFormError("Please correct the errors above and try again.");
      this.focusFirstError();
      return;
    }

    // If validation passes, proceed with form submission
    this.submitForm();
  }

  focusFirstError() {
    // Focus on the first field with an error for better accessibility
    const firstErrorField = this.form.querySelector(".error");
    if (firstErrorField) {
      firstErrorField.focus();
    }
  }

  collectFormDataAsJSON() {
    // Collect form data and convert to JSON object
    const formData = {};
    const inputs = this.form.querySelectorAll("input, select");

    inputs.forEach((input) => {
      if (input.name && input.value.trim()) {
        formData[input.name] = input.value.trim();
      }
    });

    return formData;
  }

  async submitForm() {
    const submitButton = this.form.querySelector('button[type="submit"]');

    // Collect form data as JSON object
    const formData = this.collectFormDataAsJSON();

    // Set submitting flag
    this.isSubmitting = true;

    // Show loading state
    submitButton.disabled = true;
    submitButton.classList.add("loading");
    submitButton.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(this.form.action, {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, show clear error message
        if (response.ok) {
          throw new Error(
            "Server returned an invalid response format. Please try again."
          );
        }
        throw new Error("Server error occurred. Please try again later.");
      }

      if (response.ok && result.status === "success") {
        this.showFormSuccess(
          result.message ||
            "Registration successful! Check your email for confirmation."
        );
        this.form.reset();
        this.clearAllFieldErrors();

        // Announce success to screen readers
        this.announceToScreenReader(
          "Registration successful! Check your email for confirmation."
        );

        // Redirect to success page after a brief delay to allow screen reader announcement
        setTimeout(() => {
          window.location.href = "/register-success.html";
        }, 200);
      } else {
        // Handle server-side validation errors
        if (result.errors && typeof result.errors === "object") {
          this.handleServerValidationErrors(result.errors);
        } else {
          throw new Error(
            result.message || "Registration failed. Please try again."
          );
        }
      }
    } catch (error) {
      // Handle network errors or other issues without fallback
      let errorMessage = "An error occurred. Please try again.";

      if (error.name === "TypeError" && error.message.includes("fetch")) {
        errorMessage =
          "Network connection failed. Please check your internet connection and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.showFormError(errorMessage);

      // Announce error to screen readers
      this.announceToScreenReader(errorMessage);
    } finally {
      // Remove loading state
      this.isSubmitting = false;
      submitButton.disabled = false;
      submitButton.classList.remove("loading");
      submitButton.removeAttribute("aria-busy");
    }
  }

  handleServerValidationErrors(errors) {
    // Display server-side validation errors on specific fields
    Object.keys(errors).forEach((fieldName) => {
      const field = this.form.querySelector(`[name="${fieldName}"]`);
      if (field && errors[fieldName].length > 0) {
        this.showFieldError(field, errors[fieldName][0]);
      }
    });

    this.showFormError("Please correct the errors and try again.");
    this.focusFirstError();
  }

  clearAllFieldErrors() {
    const fields = this.form.querySelectorAll("input, select");
    fields.forEach((field) => this.clearFieldError(field));
    this.interactedFields.clear();
  }

  showFormError(message) {
    const errorElement = document.getElementById("form-error");
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = "block";
    }
  }

  showFormSuccess(message) {
    const successElement = document.getElementById("form-success");
    if (successElement) {
      successElement.textContent = message;
      successElement.style.display = "block";
    }
  }

  clearFormMessages() {
    const errorElement = document.getElementById("form-error");
    const successElement = document.getElementById("form-success");

    if (errorElement) {
      errorElement.textContent = "";
      errorElement.style.display = "none";
    }

    if (successElement) {
      successElement.textContent = "";
      successElement.style.display = "none";
    }
  }
}

// Google OAuth Sign-In Handler
class GoogleOAuthHandler {
  constructor() {
    this.visaCategorySelect = document.getElementById("visa_category");
    this.countrySelect = document.getElementById("country");
    this.googleSignInBtn = document.getElementById("google-signin-btn");
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.googleSignInBtn) {
      this.googleSignInBtn.addEventListener("click", (e) =>
        this.handleGoogleSignIn(e)
      );
    }

    // Clear errors when visa category or country are selected
    if (this.visaCategorySelect) {
      this.visaCategorySelect.addEventListener("change", () => {
        if (this.visaCategorySelect.value) {
          this.clearFieldHighlight(this.visaCategorySelect);
        }
      });
    }

    if (this.countrySelect) {
      this.countrySelect.addEventListener("change", () => {
        if (this.countrySelect.value) {
          this.clearFieldHighlight(this.countrySelect);
        }
      });
    }
  }

  async handleGoogleSignIn(event) {
    event.preventDefault();

    const button = event.currentTarget;

    // Prevent double clicks
    if (button.disabled) {
      return;
    }

    // Get visa category and country values
    const visaCategory = this.visaCategorySelect?.value || "";
    const country = this.countrySelect?.value || "";

    // Validate that both are selected
    if (!visaCategory || !country) {
      this.highlightRequiredFields(visaCategory, country);
      return;
    }

    // Show loading state
    button.disabled = true;
    button.classList.add("loading");
    button.setAttribute("aria-busy", "true");

    try {
      // Import and call the OAuth initialization function with visa category and country
      const { initiateGoogleSignIn } = await import("./oauth-google.js");
      await initiateGoogleSignIn(visaCategory, country);
    } catch (error) {
      console.error("Failed to initiate Google sign-in:", error);

      // Show error message
      this.showError(
        error.message || "Failed to initiate sign-in. Please try again."
      );

      // Remove loading state
      button.disabled = false;
      button.classList.remove("loading");
      button.removeAttribute("aria-busy");
    }
  }

  showError(message) {
    const errorElement = document.getElementById("form-error");
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = "block";

      // Announce error to screen readers
      const announcement = document.createElement("div");
      announcement.setAttribute("aria-live", "assertive");
      announcement.setAttribute("aria-atomic", "true");
      announcement.className = "sr-only";
      announcement.textContent = message;

      document.body.appendChild(announcement);

      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }

  clearFieldHighlight(field) {
    const fieldName = field.id.replace("_", "-");
    const errorElement = document.getElementById(`${fieldName}-error`);
    if (errorElement) {
      errorElement.textContent = "";
      errorElement.style.display = "none";
    }
    field.classList.remove("error");
    field.removeAttribute("aria-invalid");
  }

  highlightRequiredFields(visaCategory, country) {
    // Highlight visa category field if not selected
    if (!visaCategory && this.visaCategorySelect) {
      const errorElement = document.getElementById("visa-category-error");
      if (errorElement) {
        errorElement.textContent = "Please select a visa category";
        errorElement.style.display = "block";
      }
      this.visaCategorySelect.classList.add("error");
      this.visaCategorySelect.setAttribute("aria-invalid", "true");
      this.visaCategorySelect.focus();
    }

    // Highlight country field if not selected
    if (!country && this.countrySelect) {
      const errorElement = document.getElementById("country-error");
      if (errorElement) {
        errorElement.textContent = "Please select your country";
        errorElement.style.display = "block";
      }
      this.countrySelect.classList.add("error");
      this.countrySelect.setAttribute("aria-invalid", "true");

      // Focus on country if visa category is already selected
      if (visaCategory) {
        this.countrySelect.focus();
      }
    }
  }
}

// Initialize form validation and OAuth handler when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  const registrationForm = document.getElementById("registration-form");
  if (registrationForm) {
    new RegistrationFormValidator(registrationForm);
    new GoogleOAuthHandler();
  }
});
